import React, { useEffect, useMemo, useState } from "react";
import api, { API_BASE, formatErr } from "../lib/api";
import { toast } from "sonner";

/**
 * Stage D — Layout & Piping panel.
 *
 * Sub-stage routing follows the project's authoritative
 * `stages_catalog.py` numbering:
 *   D.1 · Material Specifications (Pipe Classes)  → D2PipeClasses
 *   D.2 · Plot Plan & Equipment Layout            → D1Plot
 *   D.3 · Piping Layout & 3D Model                → D3Routing
 *   D.4 · Piping Isometrics                       → D4Isometric
 *   D.5 · Pipe Stress Analysis                    → D5Stress
 *   D.6 · Piping MTO                              → D6MTO
 *   D.7 · Clash Detection                         → D7Clash
 */
export default function StageDPanel({ projectId, subStage }) {
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [showPlot, setShowPlot] = useState(false);
  const [plotSvg, setPlotSvg] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const { data } = await api.get(
        `/projects/${projectId}/stage-d/results`,
      );
      setResults(data);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => {
    if (projectId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const run = async () => {
    setRunning(true);
    setError(null);
    try {
      await api.post(`/projects/${projectId}/stage-d/run`);
      toast.success("Stage D pipeline completed.");
      await load();
    } catch (e) {
      const msg = formatErr(e.response?.data?.detail) || e.message;
      toast.error(msg);
      setError(msg);
    } finally {
      setRunning(false);
    }
  };

  const openPlotPreview = async () => {
    setShowPlot(true);
    try {
      const r = await api.get(
        `/projects/${projectId}/stage-d/plot-plan/svg`,
        { responseType: "text" },
      );
      setPlotSvg(r.data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const res = results?.results || {};

  return (
    <div data-testid={`stage-d-panel-${subStage}`}
         className="w-full space-y-4">
      <div className="flex items-center justify-between gap-4 rounded-lg border border-neutral-800 bg-neutral-950 p-4">
        <div>
          <div className="text-base font-semibold text-neutral-100">
            Stage D · Layout & Piping
          </div>
          <div className="text-xs text-neutral-500">
            Sub-stage {subStage} · powered by D.1-D.7 gap-closure engine
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="stage-d-run" onClick={run} disabled={running}
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-500 disabled:opacity-50">
            {running ? "Running…" : (results?.cached ? "Re-run pipeline" : "Run Stage D pipeline")}
          </button>
          <button data-testid="stage-d-refresh" onClick={load}
            className="rounded-md border border-neutral-700 bg-neutral-900 px-3 py-1.5 text-xs text-neutral-200 hover:bg-neutral-800">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="stage-d-error"
             className="rounded-md border border-red-800 bg-red-950/60 p-3 text-xs text-red-200">
          {error}
        </div>
      )}
      {!results?.cached && (
        <div data-testid="stage-d-empty"
             className="rounded-md border border-amber-800 bg-amber-950/40 p-3 text-xs text-amber-100">
          No cached Stage D results. Click <b>Run Stage D pipeline</b> to build the plot plan, run spacing, stress, MTO and clash checks.
        </div>
      )}

      {results?.cached && (
        <>
          <StatsStrip res={res} />
          {subStage === "D.1" && <D2PipeClasses projectId={projectId} />}
          {subStage === "D.2" && (
            <D1Plot res={res} projectId={projectId}
                     openPlotPreview={openPlotPreview}
                     plotSvg={plotSvg} showPlot={showPlot} />
          )}
          {subStage === "D.3" && <D3Routing />}
          {subStage === "D.4" && <D4Isometric />}
          {subStage === "D.5" && <D5Stress res={res} />}
          {subStage === "D.6" && <D6MTO res={res} />}
          {subStage === "D.7" && <D7Clash />}
        </>
      )}
    </div>
  );
}

function StatsStrip({ res }) {
  const s = [
    { k: "Spacing violations", n: (res.spacing_violations || []).length,
        c: (res.spacing_violations || []).length ? "text-red-400" : "text-emerald-400" },
    { k: "Adjusted remaining", n: (res.spacing_adjusted_remaining || []).length,
        c: (res.spacing_adjusted_remaining || []).length ? "text-amber-400" : "text-emerald-400" },
    { k: "Rack tiers", n: res.pipe_rack?.main_rack?.tiers || "-" },
    { k: "Access roads", n: (res.access_roads?.primary || []).length + 1 },
    { k: "Escape routes", n: res.escape_routes?.n_usable || 0 },
    { k: "Underground svc", n: (res.underground?.networks || []).length },
    { k: "MTO lines", n: (res.mto_detail || []).length },
    { k: "Bulk items", n: (res.mto_bulk || []).length },
    { k: "Stress checks", n: res.stress_sample ? 4 : 0 },
  ];
  return (
    <div data-testid="stage-d-stats"
         className="grid grid-cols-3 gap-2 sm:grid-cols-9">
      {s.map((x) => (
        <div key={x.k} className="rounded-md border border-neutral-800 bg-neutral-950 p-2 text-center">
          <div className={`text-lg font-semibold ${x.c || "text-neutral-100"}`}>{x.n}</div>
          <div className="text-[10px] uppercase tracking-wide text-neutral-500">{x.k}</div>
        </div>
      ))}
    </div>
  );
}

function D1Plot({ res, projectId, openPlotPreview, plotSvg, showPlot }) {
  const violations = res.spacing_violations || [];
  const wind = res.wind_rose || {};
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-neutral-800 bg-neutral-950 p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-semibold text-neutral-200">Plot Plan Preview (SVG · DXF)</div>
          <div className="flex gap-2">
            <button data-testid="d1-plot-preview" onClick={openPlotPreview}
              className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-xs text-neutral-200 hover:bg-neutral-800">
              {showPlot ? "Refresh preview" : "Preview SVG"}
            </button>
            <a data-testid="d1-plot-dxf"
                href={`${API_BASE}/projects/${projectId}/stage-d/plot-plan/dxf`}
                className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-xs text-neutral-200 hover:bg-neutral-800">
                Download DXF
            </a>
          </div>
        </div>
        {plotSvg && (
          <div className="rounded-md bg-white p-2"
                dangerouslySetInnerHTML={{ __html: plotSvg }} />
        )}
      </div>
      <SectionTable title="Spacing violations"
        rows={violations.slice(0, 20)}
        cols={[["eq_a","A"],["eq_b","B"],["rule_key","Rule"],["actual_m","Actual"],["required_m","Required"],["shortfall_m","Shortfall"],["severity","Sev"]]} />
      <SectionKV title="Wind rose analysis"
        kv={[["Wind from", `${wind.prevailing_wind_from_deg}° (${wind.wind_direction_label})`],
              ["Process center", (wind.process_center || []).join(", ")]]} />
      {(wind.checks || []).length > 0 && (
        <SectionTable title="Wind-position checks"
          rows={wind.checks} cols={[["equipment","Equipment"],["type","Type"],["should_be","Should be"],["check","Check"]]} />
      )}
      <SectionKV title="Escape routes"
        kv={[["Usable", `${res.escape_routes?.n_usable}/4`],["Check", res.escape_routes?.check]]} />
      <SectionKV title="Access roads"
        kv={[["Perimeter width", `${res.access_roads?.perimeter?.width_m} m`],
              ["Turnarounds", (res.access_roads?.turnarounds || []).length],
              ["Escape routes", (res.access_roads?.escape_routes || []).length]]} />
      <SectionKV title="Pipe rack"
        kv={[["Elevation", `${res.pipe_rack?.main_rack?.elevation_m} m`],
              ["Tiers", res.pipe_rack?.main_rack?.tiers],
              ["Pipe count", res.pipe_rack?.main_rack?.pipe_count]]} />
      <SectionTable title="Underground services"
        rows={res.underground?.networks || []}
        cols={[["name","Network"],["material","Material"],["size_DN","Size"],["cover_depth_m","Cover (m)"]]} />
    </div>
  );
}

function D2PipeClasses({ projectId }) {
  const [classes, setClasses] = useState(null);
  useEffect(() => {
    api.get(`/projects/${projectId}/stage-d/pipe-classes/expanded`)
      .then((r) => setClasses(r.data))
      .catch(() => {});
  }, [projectId]);
  if (!classes) return <div className="text-xs text-neutral-500">Loading pipe classes…</div>;
  return (
    <div className="space-y-4">
      <div className="text-sm font-semibold text-neutral-200">
        Expanded pipe classes ({Object.keys(classes.classes || {}).length})
      </div>
      <div className="overflow-x-auto rounded-lg border border-neutral-800">
        <table className="w-full text-xs">
          <thead className="bg-neutral-900 text-neutral-400">
            <tr>{["Class","Service","Rating","Material","CA (mm)","T range °C","Insulation"].map((h) => (
              <th key={h} className="px-2 py-2 text-left font-medium">{h}</th>))}</tr>
          </thead>
          <tbody>
            {Object.entries(classes.classes || {}).map(([k, v]) => (
              <tr key={k} className="border-t border-neutral-800 text-neutral-200">
                <td className="px-2 py-1.5 font-semibold">{k}</td>
                <td className="px-2 py-1.5">{v.service}</td>
                <td className="px-2 py-1.5">{v.rating_lb}#</td>
                <td className="px-2 py-1.5">{v.material}</td>
                <td className="px-2 py-1.5">{v.corrosion_allowance_mm}</td>
                <td className="px-2 py-1.5">{(v.T_range_C || []).join(" to ")}</td>
                <td className="px-2 py-1.5">{v.insulation || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function D3Routing() {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4 text-xs text-neutral-400">
      3D routing engine active. Use the <code>POST /stage-d/route-3d</code> endpoint or invoke via the Stage E 3D viewer.
      Rack elevation, orthogonal 5-waypoint routing, and eccentric reducers on pump suction are applied automatically.
    </div>
  );
}

function D4Isometric() {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4 text-xs text-neutral-400">
      Isometric generator active. Spool breaks (≤ 12 m), running dimensions, ISO fitting symbols, and full BOM are exposed via <code>POST /stage-d/iso/spool-breaks</code> and <code>/iso/bom</code>.
    </div>
  );
}

function D5Stress({ res }) {
  const s = res.stress_sample || {};
  return (
    <div className="space-y-4">
      {["sustained","expansion","occasional","flexibility"].map((k) => (
        <div key={k} className="rounded-lg border border-neutral-800 bg-neutral-950 p-4">
          <div className="mb-2 text-sm font-semibold text-neutral-200 capitalize">{k} stress</div>
          <div className="grid grid-cols-2 gap-x-6 text-xs">
            {Object.entries(s[k] || {}).map(([kk, vv]) => (
              <div key={kk} className="flex justify-between border-b border-neutral-900 py-0.5">
                <span className="text-neutral-500">{kk}</span>
                <span className="text-neutral-100">{String(vv)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function D6MTO({ res }) {
  const detail = res.mto_detail || [];
  const bulk = res.mto_bulk || [];
  const weights = res.mto_weights || [];
  return (
    <div className="space-y-4">
      <SectionTable title={`MTO detail (${detail.length} lines)`}
        rows={detail}
        cols={[["line_number","Line"],["pipe_class","Class"],["size_in","Size"],
                 ["pipe_length_m","Length (m)"],["elbow_90_count","90° ell"],
                 ["gate_valve_count","Gate"],["flange_pair_count","Flanges"],
                 ["gasket_count","Gaskets"]]} />
      <SectionTable title={`Bulk requisition (${bulk.length} items)`}
        rows={bulk.slice(0, 30)}
        cols={[["category","Category"],["size_in","Size"],
                 ["material","Material"],["quantity","Qty"],
                 ["unit","Unit"],["priority","Priority"]]} />
      <SectionTable title="Line weights"
        rows={weights}
        cols={[["line_number","Line"],["size_in","Size"],
                 ["pipe_kg","Pipe (kg)"],["fitting_kg","Fittings"],
                 ["valve_kg","Valves"],["insulation_kg","Insul"],
                 ["total_kg","Total (kg)"]]} />
    </div>
  );
}

function D7Clash() {
  return (
    <div className="rounded-md border border-neutral-800 bg-neutral-950 p-4 text-xs text-neutral-400">
      Clash detection engine active. 8 clash categories, severity ranking (critical / major / minor), and maintenance envelope lookup available via <code>POST /stage-d/clash/report</code>.
    </div>
  );
}

// ────────────────── small helpers ──────────────────
function SectionKV({ title, kv }) {
  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-950 p-4">
      <div className="mb-2 text-sm font-semibold text-neutral-200">{title}</div>
      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        {kv.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between border-b border-neutral-900 py-0.5 text-xs">
            <span className="text-neutral-500">{k}</span>
            <span className="font-medium text-neutral-100">{String(v ?? "-")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
function SectionTable({ title, rows, cols }) {
  if (!rows.length) {
    return (
      <div className="rounded-lg border border-neutral-800 bg-neutral-950 p-4">
        <div className="text-sm font-semibold text-neutral-200">{title}</div>
        <div className="text-xs text-neutral-500 mt-1">No records.</div>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <div className="text-sm font-semibold text-neutral-200">{title}</div>
      <div className="overflow-x-auto rounded-lg border border-neutral-800">
        <table className="w-full text-xs">
          <thead className="bg-neutral-900 text-neutral-400">
            <tr>{cols.map(([_, h]) => (
              <th key={h} className="px-2 py-2 text-left font-medium">{h}</th>))}</tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-neutral-800 text-neutral-200">
                {cols.map(([k]) => (
                  <td key={k} className="px-2 py-1.5">{String(r[k] ?? "-").slice(0, 60)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
